import React, { useState, useEffect, useCallback } from 'react';
import { Download, QrCode, User, Trash2 } from 'lucide-react';
import { generateLaserBadgeSVG, downloadSVG } from '../../../utils/laserBadge';

const Laser = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [qrUrl, setQrUrl] = useState('https://trunk.treehacks.com/p/');

  // Queue state
  const [queue, setQueue] = useState([]);
  const [queueError, setQueueError] = useState(null);
  const [laserCount, setLaserCount] = useState(2); // 2 or 3 lasers
  const [laserFilter, setLaserFilter] = useState('all'); // 'all' | '1' | '2' | '3'

  const canGenerate = firstName.trim().length > 0;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    const effectiveQrUrl = qrUrl.trim().replace(/\/+$/, '') === 'https://trunk.treehacks.com/p' ? '' : qrUrl.trim();
    const svg = await generateLaserBadgeSVG(
      firstName.trim(),
      lastName.trim(),
      effectiveQrUrl
    );
    const fileName = `badge-${firstName.trim().toLowerCase()}${
      lastName.trim() ? `-${lastName.trim().toLowerCase()}` : ''
    }.svg`;
    downloadSVG(svg, fileName);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && canGenerate) {
      handleGenerate();
    }
  };

  // ── Queue polling ─────────────────────────────────────────────────────
  const fetchQueue = useCallback(async () => {
    try {
      const res = await fetch('/api/laser-queue');
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setQueue(data);
      setQueueError(null);
    } catch (err) {
      setQueueError('Could not load queue');
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 1000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  // ── Click a queue entry: download SVG then remove from queue ──────────
  const handleQueueClick = async (entry) => {
    const qrUrl = `https://trunk.treehacks.com/p/${entry.id}`;
    const svg = await generateLaserBadgeSVG(
      entry.firstName,
      entry.lastName,
      qrUrl
    );
    const fileName = `badge-${entry.firstName.toLowerCase()}${
      entry.lastName ? `-${entry.lastName.toLowerCase()}` : ''
    }.svg`;
    downloadSVG(svg, fileName);

    // Remove from queue (fire-and-forget, next poll will reflect)
    fetch(`/api/laser-queue?id=${encodeURIComponent(entry.id)}`, {
      method: 'DELETE',
    }).catch(() => {});
    // Optimistically remove from local state
    setQueue((prev) => prev.filter((e) => e.id !== entry.id));
  };

  // ── Remove without downloading ────────────────────────────────────────
  const handleQueueRemove = async (e, entry) => {
    e.stopPropagation();
    fetch(`/api/laser-queue?id=${encodeURIComponent(entry.id)}`, {
      method: 'DELETE',
    }).catch(() => {});
    setQueue((prev) => prev.filter((item) => item.id !== entry.id));
  };

  // ── Filter queue by laser assignment ────────────────────────────────
  // MongoDB ObjectIDs are 24-char hex strings ending with an incrementing
  // counter, so parsing the last few hex digits as a number and modding
  // by laserCount gives a perfectly alternating, stable assignment.
  const laserForEntry = (entry) => {
    const hexTail = entry.id.slice(-6); // last 6 hex chars (counter portion)
    return (parseInt(hexTail, 16) % laserCount) + 1; // 1-based laser number
  };

  const filteredQueue =
    laserFilter === 'all'
      ? queue
      : queue.filter((entry) => laserForEntry(entry) === Number(laserFilter));

  return (
    <div className='max-w-2xl mx-auto p-6 space-y-8'>
      {/* ── Manual badge generator ─────────────────────────────────────── */}
      <div>
        <div className='flex items-center gap-3 mb-6'>
          <h1 className='font-bold text-3xl'>Laser Badge</h1>
        </div>

        <div className='bg-white rounded-lg shadow p-6 space-y-5'>
          {/* First Name */}
          <div>
            <label className='flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1'>
              <User className='h-4 w-4 text-gray-500' />
              <span>First Name *</span>
            </label>
            <input
              type='text'
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='e.g. Jane'
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg'
            />
          </div>

          {/* Last Name */}
          <div>
            <label className='flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1'>
              <User className='h-4 w-4 text-gray-500' />
              <span>Last Name</span>
            </label>
            <input
              type='text'
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='e.g. Doe'
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg'
            />
          </div>

          {/* QR Code URL */}
          <div>
            <label className='flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1'>
              <QrCode className='h-4 w-4 text-gray-500' />
              <span>QR Code URL</span>
            </label>
            <input
              type='text'
              value={qrUrl}
              onChange={(e) => setQrUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='https://trunk.treehacks.com/p/...'
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono'
            />
            <p className='text-xs text-gray-400 mt-1'>
              Leave empty to generate a badge without a QR code.
            </p>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={!canGenerate}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white text-lg flex items-center justify-center space-x-2
              ${
                canGenerate
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}>
            <Download className='h-5 w-5' />
            <span>Download SVG</span>
          </button>
        </div>
      </div>

      {/* ── Badge Queue ────────────────────────────────────────────────── */}
      <div>
        <div className='flex items-center justify-between mb-4'>
          <h2 className='font-bold text-2xl'>Badge Queue</h2>
          <span className='text-sm text-gray-500'>
            {filteredQueue.length} of {queue.length}{' '}
            {queue.length === 1 ? 'badge' : 'badges'} waiting
          </span>
        </div>

        {/* Laser count mode */}
        <div className='flex rounded-lg overflow-hidden border border-gray-300 mb-3'>
          {[2, 3].map((count) => (
            <button
              key={count}
              onClick={() => {
                setLaserCount(count);
                // Reset filter if current selection is invalid for new count
                if (laserFilter !== 'all' && Number(laserFilter) > count) {
                  setLaserFilter('all');
                }
              }}
              className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                laserCount === count
                  ? 'bg-gray-800 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}>
              {count} Lasers
            </button>
          ))}
        </div>

        {/* Laser filter toggle */}
        <div className='flex rounded-lg overflow-hidden border border-gray-300 mb-4'>
          {[
            { value: 'all', label: 'All' },
            ...Array.from({ length: laserCount }, (_, i) => ({
              value: String(i + 1),
              label: `Laser ${i + 1}`,
            })),
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setLaserFilter(opt.value)}
              className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                laserFilter === opt.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>

        {queueError ? (
          <div className='bg-red-50 text-red-600 rounded-lg p-4 text-sm'>
            {queueError}
          </div>
        ) : filteredQueue.length === 0 ? (
          <div className='bg-gray-50 rounded-lg p-8 text-center text-gray-400'>
            {queue.length === 0
              ? 'Queue is empty. Badges appear here when hackers are scanned at check-in.'
              : 'No badges assigned to this laser.'}
          </div>
        ) : (
          <div className='space-y-2'>
            {filteredQueue.map((entry) => (
              <button
                key={entry.id}
                onClick={() => handleQueueClick(entry)}
                className='w-full bg-white rounded-lg shadow hover:shadow-md hover:bg-blue-50 transition-all p-4 flex items-center justify-between group text-left'>
                <div className='flex items-center space-x-3'>
                  <Download className='h-5 w-5 text-gray-300 group-hover:text-blue-600 transition-colors' />
                  <div>
                    <p className='font-medium text-gray-900'>
                      {entry.firstName}
                      {entry.lastName ? ` ${entry.lastName}` : ''}
                    </p>
                    <p className='text-xs text-gray-400'>
                      {new Date(entry.queuedAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <div
                  onClick={(e) => handleQueueRemove(e, entry)}
                  className='p-2 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors'
                  title='Remove without downloading'>
                  <Trash2 className='h-4 w-4' />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Laser;
